/**
 * The phone remote's native side (PhoneRemotePlugin.java, roadmap Section 25),
 * as the page sees it. Only inside the APK; on the website every call answers
 * null and nothing listens.
 */

export interface PairedPhone {
  id: string;
  name: string;
  pairedAt: number;
  lastSeen: number;
  connected: boolean;
}

export interface PhoneRemoteStatus {
  enabled: boolean;
  running: boolean;
  error?: string;
  /** This TV's home-network address, or null without a network. */
  address: string | null;
  port: number;
  /** The phone page's address, for the QR code. */
  url: string | null;
  /** The same page by this TV's own name, which stays when its address changes. */
  localUrl?: string;
  tv: string;
  /** The six digits on screen while pairing is open. */
  pairingCode: string | null;
  pairingLeftMs: number;
  phones: PairedPhone[];
  /** Phones connected right now. */
  connected: number;
  /** Set on the "phones" event when a phone has just connected. */
  joined?: string;
}

export interface PhoneRequest {
  session: number;
  id: string;
  op: string;
  args: Record<string, unknown>;
}

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  PluginHeaders?: Array<{ name: string }>;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
  addListener?: (plugin: string, event: string, callback: (data: never) => void) => { remove: () => unknown };
}

const PLUGIN = "PhoneRemote";

function capacitor(): CapacitorLike | null {
  const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  if (cap?.isNativePlatform?.() !== true || typeof cap.nativePromise !== "function") return null;
  if (!cap.PluginHeaders?.some((header) => header.name === PLUGIN)) return null;
  return cap;
}

/** True inside an app that has the phone remote. */
export function phoneRemoteAvailable(): boolean {
  return capacitor() !== null;
}

async function call<T>(method: string, options: Record<string, unknown> = {}): Promise<T | null> {
  const cap = capacitor();
  if (!cap) return null;
  return (await cap.nativePromise!(PLUGIN, method, options)) as T;
}

export const phoneRemote = {
  status: () => call<PhoneRemoteStatus>("status"),
  setEnabled: (enabled: boolean) => call<PhoneRemoteStatus>("setEnabled", { enabled }),
  startPairing: () => call<PhoneRemoteStatus>("startPairing"),
  cancelPairing: () => call<PhoneRemoteStatus>("cancelPairing"),
  revoke: (id: string) => call<PhoneRemoteStatus>("revoke", { id }),
  publishState: (state: Record<string, unknown>) => call<void>("publishState", { state }),
  reply: (session: number, id: string, data: Record<string, unknown>) => call<void>("reply", { session, id, data }),
  answerPairRequest: (id: string, allow: boolean) => call<PhoneRemoteStatus & { answered: boolean }>("answerPairRequest", { id, allow }),
};

/** A phone asking to pair without a code (native PairRequests). */
export interface PairRequest {
  id: string;
  name: string;
  leftMs: number;
  /** Six digits the phone can type instead of a press on the TV (shown on the TV only). */
  code?: string;
}

/** Listens to the plugin: "phones" (status), "action" ({id}), "request" (PhoneRequest), "pairRequest" (PairRequest). */
export function onPhoneRemote(event: "phones", callback: (status: PhoneRemoteStatus) => void): () => void;
export function onPhoneRemote(event: "action", callback: (data: { id: string }) => void): () => void;
export function onPhoneRemote(event: "request", callback: (data: PhoneRequest) => void): () => void;
export function onPhoneRemote(event: "pairRequest", callback: (data: PairRequest) => void): () => void;
/** The question was answered another way (its code was typed on the phone): take it off the screen. */
export function onPhoneRemote(event: "pairRequestClosed", callback: (data: { id: string }) => void): () => void;
export function onPhoneRemote(event: string, callback: (data: never) => void): () => void {
  const cap = capacitor();
  if (!cap || typeof cap.addListener !== "function") return () => undefined;
  const handle = cap.addListener(PLUGIN, event, callback);
  return () => void handle.remove();
}
